import { signal, WritableSignal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Extension } from "ng2-adsk-forge-viewer";
import { BehaviorSubject } from 'rxjs';
import { ColliderShape } from 'src/app/classes/viewer/shapes/collider';
import { defaultModel, Model } from 'src/app/interfaces/api/model';
import { Track } from 'src/app/interfaces/api/track';
import { Tracker } from 'src/app/interfaces/api/tracker';
import { Unit } from 'src/app/interfaces/api/unit';
import { ModelService } from 'src/app/services/api/models/model.service';
import { UnitService } from 'src/app/services/api/units/unit.service';
// import { Font, FontLoader } from 'three/examples/jsm/loaders/FontLoader.js';
import { Config } from '../../../interfaces/shared/config';
import { UnitShape } from '../shapes/unit';
import { UnitDockingPanel } from '../units/unit-docking-panel';
import { TrackerService } from './../../../services/api/trackers/tracker.service';
import { TrackService } from './../../../services/api/tracks/track.service';
import { STLLoader } from './STLLoader';

export class UnitExtension extends Extension {
    public static override extensionName: string = 'UnitExtension';
    private subToolbar: any;
    private dockingPanel: UnitDockingPanel | null = null;
    public translate!: TranslateService;
    public trackerService!: TrackerService;
    public trackService!: TrackService;
    public modelService!: ModelService;
    public unitService!: UnitService;
    public model: Model = defaultModel;
    public origin: any;

    public unitSignal: WritableSignal<UnitShape[]> = signal([]);

    units$!: BehaviorSubject<Unit[]>;
    is2d: boolean = false;
    scaleDivider: number = 25;
    unitScaleMultiplier: number = 1;
    callback: any;
    panel: any = null;
    colliders: ColliderShape[] = [];
    unitMeshes: any = {};
    colors = Config.colors;
    icons: any;
    player: any;
    playerData: BehaviorSubject<any> = new BehaviorSubject<any>(null);
    unitScaleFactor: number = 0.1;
    public elementInfoExt: any = null;
    unitButton!: any;

    colliderMaterial: THREE.MeshPhongMaterial = new THREE.MeshPhongMaterial({
        color: this.colors.danger,
        transparent: true,
        opacity: 0.5,
        side: THREE.DoubleSide,
        depthWrite: false
    });

    private toolbarCreatedListener!: () => void;
    threeFont?: any;

    get units(): UnitShape[] {
        return this.unitSignal ? this.unitSignal() : [];
    }

    public activate(): any {
        // Called when Forge Viewer loads your extension
    }

    public deactivate(): any {
        // Called when Forge Viewer unloads your extension
    }

    public load(): any {
        if (!this.viewer) {
            console.error('UnitExtension: viewer is null in load()');
            return false;
        }

        this.toolbarCreatedListener = () => this.createUI();

        this.viewer.addEventListener(Autodesk.Viewing.TOOLBAR_CREATED_EVENT, this.toolbarCreatedListener);
        return true;
    }

    public unload(): any {
        if (this.viewer && this.toolbarCreatedListener) {
            this.viewer.removeEventListener(Autodesk.Viewing.TOOLBAR_CREATED_EVENT, this.toolbarCreatedListener);
        }

        if (this.dockingPanel) {
            this.dockingPanel['setVisible'](false);
            this.dockingPanel = null;
        }

        if (this.subToolbar && this.viewer) {
            this.viewer.toolbar.removeControl(this.subToolbar);
            this.subToolbar = null;
        }

        return true;
    }

    createUI() {
        const button = new Autodesk.Viewing.UI.Button('units-btn');
        this.unitButton = button;

        button.addClass('units-btn');
        button.setToolTip('units');
        button.setIcon('units-btn-icon');

        button.onMouseOver = () => {
            button.setIcon('hover-units-btn-icon');
        }

        button.onMouseOut = () => {
            button.setIcon('units-btn-icon');
        }

        button.onClick = () => {
            this.onToolbarButtonClicked();
            this.updateButtonActiveState();
        }

        this.subToolbar = this.viewer.toolbar.getControl('unifield-toolbar') ?? new Autodesk.Viewing.UI.ControlGroup('unifield-toolbar');

        this.subToolbar.addControl(button);
        this.viewer.toolbar.addControl(this.subToolbar);

        const styleId = 'unit-extension-style';
        if (!document.getElementById(styleId)) {
            const link = document.createElement('link');
            link.id = styleId;
            link.rel = 'stylesheet';
            link.href = 'viewer/units/unit-extension/unit-extension.css';
            document.head.appendChild(link);
        }
    };

    async init() {
        await this.loadIcons();
        // await this.load3DFonts();

        this.initOverlay();

        this.units$.subscribe({
            next: (units: Unit[]) => {

                if (!this.units || this.units.length === 0) {
                    this.initUnits();
                    return;
                }

                let needInvalidate: boolean = false;

                units.forEach((u: Unit) => {
                    const existing = this.units.find(s => s.id === u.id);
                    if (!existing) return;

                    existing.show = u.visible;
                    existing.visible = u.visible;
                    existing.showCollider = u.collider.visible ?? false;
                    existing.showTrace = u.trace ?? false;
                    existing.color = u.color;

                    if (this.viewer && this.viewer.impl) {
                        if (existing.mesh) {
                            existing.mesh.visible = u.visible;

                            if (u.visible) {
                                this.viewer.impl.addOverlay(existing.color, existing.mesh);
                            } else {
                                this.viewer.impl.removeOverlay(existing.color, existing.mesh);
                            }

                            needInvalidate = true;
                        }

                        if (existing.collider?.mesh) {
                            existing.collider.mesh.visible = u.collider.visible ?? false;
                            if (u.collider.visible) {
                                this.viewer.impl.addOverlay('collider', existing.collider.mesh);
                            } else {
                                this.viewer.impl.removeOverlay('collider', existing.collider.mesh);
                            }

                            needInvalidate = true;
                        }

                        if (needInvalidate) {
                            this.viewer.impl.invalidate(true, true, true);
                            this.viewer.impl.sceneUpdated(true);
                        }
                    }
                });
            },
            error: (err) => {
                console.error('🔥 UnitExtension: Error subscribing to units$:', err);
            }
        });

        if (this.viewer && this.viewer.model.getData().is2d) {
            this.is2d = true;
            this.scaleDivider = 15;
        }

        if (this.dockingPanel && this.dockingPanel['isVisible']()) {
            this.dockingPanel.init();
        }
    }

    dockPanel() {
        if (!this.dockingPanel) {
            this.dockingPanel = new UnitDockingPanel(this.viewer, this.viewer.container, 'unitcontainer', 'Units',
                {
                    trackerService: this.trackerService,
                    trackService: this.trackService,
                    modelService: this.modelService,
                    unitService: this.unitService,
                    updateFromTrack: this.updateFromTrack.bind(this),
                    translate: this.translate,
                    playerData: this.playerData,
                    player: this.player,
                    model: this.model,
                    unitSignal: this.unitSignal,
                    unitScaleMultiplier: this.unitScaleMultiplier,
                    onScaleDividerChange: (value: number) => { this.unitScaleMultiplier = value; },
                    projectXY: (point: any) => point, // Placeholder
                    projectLatLonXY: (point: any) => point, // Placeholder
                    updateUnits: () => { }, // Placeholder
                    offset: null // Placeholder
                });
        }

        // Wait for the DOM is ready
        setTimeout(() => {
            const titleDiv = (this.dockingPanel as any)?.container?.querySelector('.docking-panel-title');

            if (titleDiv && !titleDiv.textContent?.trim()) {
                titleDiv.textContent = "Units";
            }
        }, 0);

        this.dockingPanel['setVisible'](true);

        (this.dockingPanel as any).addVisibilityListener?.((visible: boolean) => {
            if (!visible) {
                this.dockingPanel = null;
            }

            this.updateButtonActiveState();
        });
    }

    // load3DFonts(): Promise<void> {
    //     return new Promise((resolve, reject) => {
    //         const loader = new FontLoader();

    //         loader.load(
    //             'https://threejs.org/examples/fonts/helvetiker_regular.typeface.json',
    //             (font: Font) => {
    //                 this.threeFont = font;
    //                 resolve();
    //             },
    //             undefined,
    //             (error: unknown) => {
    //                 console.error('❌ Error loading font:', error);
    //                 reject(error);
    //             }
    //         );
    //     });
    // }

    loadIcons() {
        return new Promise((resolve, reject) => {
            try {
                const models: string[] = [
                    'worker.stl', 'hook.stl', 'formwork.stl', 'formwork-2.stl', 'wheel-1.stl',
                    'wheel.stl', 'warning.stl', 'platform.stl', 'custom.stl',
                    'tower-base.stl', 'tower-part.stl', 'jib-base.stl', 'jib-part.stl'
                ];

                let loadCount: number = 0;
                let failCount: number = 0;

                if (!this.icons) {
                    this.initOverlay();
                    this.icons = {};

                    models.forEach((model) => {
                        const stlLoader = new STLLoader();

                        stlLoader.load('/img/viewer/3d-model/' + model, (geometry: THREE.Geometry) => {
                            loadCount++;

                            try {
                                if (typeof geometry.computeFaceNormals === 'function') {
                                    geometry.computeFaceNormals();
                                }

                                if (typeof geometry.computeVertexNormals === 'function') {
                                    geometry.computeVertexNormals();
                                }

                                let bufferGeometry: any;

                                if (geometry.type !== "BufferGeometry") {
                                    const tempBufferGeometry = new THREE.BufferGeometry();
                                    if (typeof tempBufferGeometry.fromGeometry === 'function') {
                                        bufferGeometry = tempBufferGeometry.fromGeometry(geometry);
                                    } else {
                                        console.warn(`UnitExtension: fromGeometry not available for ${model}, using fallback`);
                                        bufferGeometry = new THREE.BufferGeometry().copy(geometry as any);
                                    }
                                } else {
                                    bufferGeometry = geometry as any;
                                }

                                if (!this.isValidBufferGeometry(bufferGeometry)) {
                                    console.error(`UnitExtension: Invalid BufferGeometry for ${model}!`);
                                    failCount++;
                                } else {
                                    this.icons[model] = bufferGeometry;
                                }

                                if (loadCount + failCount >= models.length) {
                                    console.log(`All models processed: ${loadCount} loaded, ${failCount} failed`);
                                    resolve(true);
                                }

                            } catch (conversionError) {
                                console.error(`UnitExtension: Error processing geometry for ${model}:`, conversionError);
                                failCount++;

                                if (loadCount + failCount >= models.length) {
                                    console.log(`All models processed after error: ${loadCount} loaded, ${failCount} failed`);
                                    resolve(true);
                                }
                            }

                        }, (progress) => {
                            // Progression silencieuse
                        }, (error: any) => {
                            loadCount++;
                            failCount++;
                            console.error(`UnitExtension: Failed to load ${model}:`, error);

                            if (loadCount + failCount >= models.length) {
                                console.log(`All models processed after load error: ${loadCount} loaded, ${failCount} failed`);
                                resolve(true);
                            }
                        });
                    });
                } else {
                    resolve(true);
                }
            } catch (e: any) {
                console.error('UnitExtension: Error in loadIcons, proceeding with initUnits anyway:', e);
                resolve(true);
            }
        })
    }

    initOverlay() {
        try {
            this.viewer.impl.createOverlayScene('collider', this.colliderMaterial);

            Object.keys(this.colors).forEach(c => {
                let material: any = new THREE.MeshPhongMaterial({
                    color: this.colors[c]
                });

                if (c == 'crane') {
                    material.transparent = true;
                    material.opacity = 0.5;
                }

                this.viewer.impl.createOverlayScene(c, material);

                let lineMaterial = new THREE.LineBasicMaterial({
                    color: this.colors[c],
                    transparent: false,
                    linewidth: 5
                });

                this.viewer.impl.createOverlayScene('trace-' + c, lineMaterial);
            });

            if (this.viewer.impl.overlayScenes) {
                const overlayNames = Object.keys(this.viewer.impl.overlayScenes);
            }

        } catch (e) {
            console.error('❌ Erreur lors de l\'initialisation des overlays:', e);
        }
    }

    initUnits() {
        const currentUnits = this.units$.getValue();
        if (!currentUnits?.length) return;

        if (this.unitSignal) {
            this.unitSignal.set(currentUnits.map(u => new UnitShape(u, this.viewer, this.model, this.icons, this.threeFont as any)));

            if (this.elementInfoExt && typeof this.elementInfoExt.onUnitsInitialized === 'function') {
                this.elementInfoExt.onUnitsInitialized(this.unitSignal());
            }

            this.lockNavigationBounds();
            this.animate();

            const unitShapes = this.unitSignal();

            for (const unit of unitShapes) {
                if (unit.lastPosition) {
                    unit.updateFromTrack(unit.lastPosition);
                }
            }

            for (const unit of unitShapes) {
                if (unit.collider?.geometry !== "Crane") continue;

                const targetTrackerId = unit.crane?.hookTracker || unit.crane?.jibTracker;
                if (!targetTrackerId) continue;

                const targetUnit = unitShapes.find(u =>
                    u.trackers?.find((t: Tracker) => t.id === targetTrackerId)
                );
                if (targetUnit?.lastPosition) {
                    unit.updateFromTarget(targetUnit.lastPosition);
                }
            }
        } else {
            console.error('❌ UnitExtension: unitSignal not provided, cannot set units!');
        }

        // let unitShapes: UnitShape[] = [];
        // if (!currentUnits || currentUnits.length === 0) {
        //     console.error('❌ No units available from Signal in initUnits!');
        //     return;
        // }

        // try {
        //     this.colliders = [];
        //     let positions = [];
        //     let validUnitsCount: number = 0;

        //     for (let i = 0; i < currentUnits.length; i++) {
        //         const currentUnit = currentUnits[i];

        //         if (!currentUnit) {
        //             console.warn(`⚠️ Unit ${i} is null or undefined`);
        //             continue;
        //         }

        //         let lastPosition = currentUnit.lastPosition;

        //         if (!lastPosition ||
        //             (lastPosition.x == undefined &&
        //                 lastPosition.y == undefined &&
        //                 lastPosition.z == undefined &&
        //                 lastPosition.lat == undefined &&
        //                 lastPosition.lon == undefined)) {
        //             currentUnit.lastPosition = {
        //                 lat: 0,
        //                 lon: 0,
        //                 x: 0,
        //                 y: 0,
        //                 z: 0
        //             }
        //         }

        //         let unit = new UnitShape(currentUnit, this.viewer, this.model, this.icons, this.threeFont as any);
        //         if (unit && unit.mesh) {

        //             validUnitsCount++;

        //             if (unit.trackers && unit.trackers.length) {
        //                 positions.push({
        //                     lat: unit.lastPosition.lat,
        //                     lon: unit.lastPosition.lon,
        //                     x: unit.lastPosition.x,
        //                     y: unit.lastPosition.y,
        //                     z: unit.lastPosition.z,
        //                     trackerName: unit.trackers[0]?.name ?? '',
        //                     tracker: unit.trackers[0]?.id ?? ''
        //                 });
        //             }
        //             unitShapes.push(unit)
        //         } else {
        //             console.error(`❌ UnitExtension: Failed to create unit ${i} - no mesh`);
        //         }
        //     }

        //     // for (let i = 0; i < unitShapes.length; i++) {

        //     //     let unit = unitShapes[i];
        //     //     if (!unit) continue;
        //     //     let targetTracker = unit.crane.hookTracker || unit.crane.jibTracker;
        //     //     if (targetTracker) {
        //     //         let targetUnit = unitShapes.find(u => u.trackers && u.trackers[0] && u.trackers[0].id == targetTracker);
        //     //         let trackers = targetUnit?.trackers;
        //     //         //to implement
        //     //         if (trackers && trackers.length && trackers[0]?.lastTrack) {
        //     //             let lastTrack = trackers[0].lastTrack;

        //     //             if (unit.updateFromTarget) unit.updateFromTarget(lastTrack)

        //     //         }
        //     //     }

        //     //     if (unit.collider.geometry == "Crane hook") {
        //     //         let craneUnit = unitShapes.find(u => unit.trackers && unit.trackers[0] &&
        //     //             (unit.trackers[0].id == u.crane.hookTracker ||
        //     //                 unit.trackers[0].id == u.crane.jibTracker)
        //     //         )

        //     //         if (craneUnit) {
        //     //             if (unit.colliders[0] && craneUnit.colliders[0])
        //     //                 unit.colliders[0].craneCollider = craneUnit.colliders[0];
        //     //         }
        //     //     }

        //     // }

        //     this.unitSignal?.set(unitShapes)
        //     if (this.viewer && this.viewer.impl) {
        //         this.viewer.impl.invalidate(true, true, true);
        //         this.viewer.impl.sceneUpdated(true);
        //     }

        //     // if (this.elementInfoExt && typeof this.elementInfoExt.onUnitsInitialized === 'function') {
        //     //     this.elementInfoExt.onUnitsInitialized(this.units);
        //     // }

        //     // if (validUnitsCount > 0) {
        //     //     this.animate();

        //     // }

        //     positions.forEach((p: any) => this.updateFromTrack(p))

        // } catch (e: any) {
        //     console.error('💥 UnitExtension: CRITICAL ERROR in initUnits:', e);
        //     throw e;
        // }
    }

    updateUnit(newUnit: UnitShape) {
        try {
            let unit = this.units.find(u => u.id == newUnit.id);

            if (unit && unit.update) {
                unit.update(newUnit);
            }

            this.viewer.impl.invalidate(true, true, true);
            // this.viewer.impl.sceneUpdated(true);
        } catch (e) {
            console.error(e)
        }
    }

    public updateFromTrack(track: Track) {
        try {
            let trackerId: string;

            if (!track) return;

            if (typeof track.tracker === 'object' && track.tracker !== null && 'id' in track.tracker)
                trackerId = track.tracker.id;
            else
                trackerId = track.tracker;

            let unit = this.units.find(u => u.trackers.findIndex((t: { id: string; }) => t.id == trackerId) >= 0);
            if (!unit) return;

            if (unit && unit.updateFromTrack) unit.updateFromTrack({ ...track });

            let craneUnit = this.units.find(u => (u?.collider?.geometry == "Crane" && u.crane.hookTracker === trackerId || u.crane.jibTracker === trackerId));

            if (craneUnit && craneUnit.updateFromTarget) {
                craneUnit.updateFromTarget({ ...track });

                if (unit?.collider?.geometry == "Crane hook") {
                    unit.colliders.forEach((c: ColliderShape) => {
                        c.transformCraneHook();
                    })
                }

                craneUnit.mesh.rotation.z = craneUnit.model.origin.angle * (Math.PI / 180);
            }

            if (unit?.collider?.geometry == "Crane") {
                let target: UnitShape | null = null;

                if (unit.crane.hookTracker) {
                    target = this.units.find(u => u.trackers.find((t: Tracker) => t.id == unit.crane.hookTracker)) || null;
                } else if (unit.crane.jibTracker) {
                    target = this.units.find(u => u.trackers.find((t: Tracker) => t.id == unit.crane.jibTracker)) || null;
                }

                if (target) {
                    unit.updateFromTarget(target.lastPosition)
                }
            }

            this.viewer.impl.invalidate(true, true, true);
            this.viewer.impl.sceneUpdated(true);
        } catch (e) {
            console.error(e)
        }
    }

    lockNavigationBounds() {
        try {
            const bbox = this.viewer.model.getBoundingBox();
            const impl = this.viewer.impl as any;
            const pad = new THREE.Vector3(400, 400, 400);

            const paddedBbox = new THREE.Box3(
                bbox.min.clone().sub(pad),
                bbox.max.clone().add(pad)
            );

            if (impl.getVisibleBounds) {
                impl.getVisibleBounds = () => paddedBbox;
            }
        } catch (e) {
            console.error('lockNavigationBounds error:', e);
        }
    }

    animate() {
        if (!this.viewer || !this.viewer.impl) return;

        try {
            const camera: THREE.Camera = this.viewer.getCamera();
            if (!camera || !camera.position) return;

            const currentUnits = this.unitSignal ? this.unitSignal() : [];

            currentUnits.forEach((u, index) => {
                const isCrane = u.geometry === "Crane" || u.collider?.geometry === "Crane";

                if (!u || isCrane || !u.mesh) {
                    return;
                }

                if (!u.mesh.position) {
                    console.warn(`⚠️ UnitExtension: Unit ${index} mesh has no position`);
                    return;
                }

                try {
                    let dist: number = u.mesh.position.distanceTo(camera.position);
                    let scale: number = 1;

                    if (!this.is2d) {
                        scale = (dist / this.scaleDivider) * this.unitScaleMultiplier;

                        if (scale < this.model.minUnitScale) {
                            return;
                        }

                        let vector: THREE.Vector3, angle: number;
                        let target = new THREE.Vector3(camera.position.x, camera.position.y, camera.position.z);
                        let pos = new THREE.Vector3(u.mesh.position.x, u.mesh.position.y, u.mesh.position.z)
                        vector = target.sub(pos);
                        angle = Math.atan2(vector.y, vector.x);

                        if (u.mesh.children[0]) {
                            u.mesh.children[0].rotation["y"] = angle;
                        }
                    } else {
                        scale = camera.position.z / this.scaleDivider;
                    }

                    u.mesh.scale.set(scale, scale, scale);
                    u.mesh.updateMatrix();

                } catch (unitAnimationError) {
                    console.error(`💥 UnitExtension: Error animating unit ${index}:`, unitAnimationError);
                }
            });

        } catch (animationError) {
            console.error('💥 UnitExtension: Animation error:', animationError);
            return;
        }

        requestAnimationFrame(() => this.animate());
    }

    private isValidBufferGeometry(geometry: THREE.BufferGeometry): boolean {
        if (!geometry) {
            console.error('❌ UnitExtension: Geometry is null or undefined');
            return false;
        }

        if (!geometry.attributes) {
            console.error('❌ UnitExtension: Geometry has no attributes');
            return false;
        }

        const positionAttr = (geometry.attributes as any)['position'];
        if (!positionAttr) {
            console.error('❌ UnitExtension: Geometry missing position attribute');
            return false;
        }

        if (!positionAttr.array) {
            console.error('❌ UnitExtension: Position attribute has no array');
            return false;
        }

        if (positionAttr.array.length === 0) {
            console.error('❌ UnitExtension: Position attribute array is empty');
            return false;
        }

        if (typeof positionAttr.count !== 'number' || positionAttr.count < 3) {
            console.error('❌ UnitExtension: Not enough vertices for a valid geometry:', positionAttr.count);
            return false;
        }

        return true;
    }

    onToolbarButtonClicked() {
        if (this.dockingPanel != null) {
            if (this.dockingPanel && typeof (this.dockingPanel as any).uninitialize === 'function') {
                (this.dockingPanel as any).uninitialize();
            }

            this.dockingPanel = null;
        } else {
            this.dockPanel();
        }

        this.updateButtonActiveState();
    }

    private updateButtonActiveState() {
        const active = !!this.dockingPanel;

        if (this.unitButton) {
            this.unitButton.setState(active ? Autodesk.Viewing.UI.Button.State.ACTIVE : Autodesk.Viewing.UI.Button.State.INACTIVE);
        }

        const buttonElement = document.getElementById('units-btn');
        if (buttonElement) {
            buttonElement.classList.toggle('active', active);
            buttonElement.classList.toggle('inactive', !active);
        }
    }
}
